import * as R from 'ramda';
import { dimensionValueDisplay } from '../dimension-utils';
import { getFormatedObservation } from '../observation-formater';

function dimensionValueIsValid(dimensionValue, chartDimension) {
  return R.includes(
    R.prop('id', dimensionValue),
    R.map(k => R.prop(`${k}Id`, chartDimension), ['x', 'y']),
  );
}

function getDimensionValues(
  dimensionValuesIndexes,
  dimensions,
  dimensionsWithValuesIndexedById,
  chartDimensionId,
  display,
) {
  const dimensionsWithValuesIds = R.keys(dimensionsWithValuesIndexedById);
  return R.addIndex(R.reduce)(
    (acc, dvi, i) => {
      const dimension = R.nth(i, dimensions);
      const dimensionId = R.prop('id', dimension);
      const dimensionValue = R.nth(dvi, R.propOr([], 'values', dimension || {}));
      if (dimensionId !== chartDimensionId && R.includes(dimensionId, dimensionsWithValuesIds)) {
        return R.assoc(
          dimensionId,
          { ...dimensionValue, name: dimensionValueDisplay(display)(dimensionValue) },
          acc,
        );
      }
      return acc;
    },
    {},
    dimensionValuesIndexes,
  );
}

function getDimensionValueKey(chartDimension, dimensionValue) {
  return R.prop('xId', chartDimension) === R.prop('id', dimensionValue) ? 'x' : 'y';
}

function datapoints(
  observations,
  dimensions,
  dimensionsWithValuesIndexedById,
  chartDimension,
  display,
  formaterAttrs,
) {
  const chartDimensionId = R.prop('id', chartDimension);
  const chartDimensionIndex = R.findIndex(R.propEq(chartDimensionId, 'id'), dimensions);
  const datapoints = R.reduce(
    (acc, k) => {
      const o = R.prop(k, observations);
      const oDimensionValuesIndexes = R.split(':', k);
      const oDimensionValueIndex = oDimensionValuesIndexes[chartDimensionIndex];
      const _chartDimension = R.nth(chartDimensionIndex, dimensions);
      const oDimensionValue = R.nth(
        oDimensionValueIndex,
        R.propOr([], 'values', _chartDimension || {}),
      );
      if (dimensionValueIsValid(oDimensionValue, chartDimension)) {
        const oPartialKey = R.pipe(
          R.addIndex(R.reject)((_, i) => i === chartDimensionIndex),
          R.join(':'),
        )(oDimensionValuesIndexes);

        if (!R.has(oPartialKey, acc)) {
          const dimensionValues = getDimensionValues(
            oDimensionValuesIndexes,
            dimensions,
            dimensionsWithValuesIndexedById,
            chartDimensionId,
            display,
          );
          acc[oPartialKey] = { dimensionValues };
        }
        const dimensionValueKey = getDimensionValueKey(chartDimension, oDimensionValue);
        acc[oPartialKey][dimensionValueKey] = R.head(o);
        acc[oPartialKey][`${dimensionValueKey}Format`] = getFormatedObservation(o, formaterAttrs);
      }
      return acc;
    },
    {},
    R.keys(observations),
  );
  // it is possible to have null observations or incomplete datapoints (just an x or y)
  return R.pipe(
    R.filter(dp => !R.isNil(dp.x) && !R.isNil(dp.y)),
    R.values,
  )(datapoints);
}

const series = (
  observations,
  dimensions,
  dimensionsWithValuesIndexedById,
  chartDimension,
  display,
  formaterAttrs,
) => {
  const values = R.pipe(
    R.prop(R.prop('id', chartDimension || {})),
    R.propOr([], 'values'),
  )(dimensionsWithValuesIndexedById);
  const xValue = R.find(R.propEq(R.prop('xId', chartDimension), 'id'), values);
  const yValue = R.find(R.propEq(R.prop('yId', chartDimension), 'id'), values);
  return [
    {
      dimensionValues: {
        x: { ...xValue, name: dimensionValueDisplay(display)(xValue) },
        y: { ...yValue, name: dimensionValueDisplay(display)(yValue) },
      },
      datapoints: datapoints(
        observations,
        dimensions,
        dimensionsWithValuesIndexedById,
        chartDimension,
        display,
        formaterAttrs,
      ),
    },
  ];
};

export default (
  { observations, dimensions },
  dimensionsWithValuesIndexedById,
  chartDimension,
  display,
  formaterAttrs,
) =>
  series(
    observations,
    dimensions,
    dimensionsWithValuesIndexedById,
    chartDimension,
    display,
    formaterAttrs,
  );
